// SPDX-License-Identifier: MIT
/**
 * Offscreen layers and track mattes — the compositing vocabulary of motion design (text revealing
 * footage, shapes cutting through each other). Layers are pooled by size and fully redrawn on every
 * use, so pooling never carries state between frames.
 */
import { instrumentLayer } from '../qa.ts';
type Ctx = CanvasRenderingContext2D;
const pool = new Map<string, OffscreenCanvas[]>();
const inUse = new Set<OffscreenCanvas>();
function acquire(w: number, h: number): OffscreenCanvas {
    const key = `${w}x${h}`;
    const free = (pool.get(key) ?? []).find((c) => !inUse.has(c));
    const canvas = free ?? new OffscreenCanvas(w, h);
    if (!free)
        pool.set(key, [...(pool.get(key) ?? []), canvas]);
    inUse.add(canvas);
    return canvas;
}
function sizeOf(ctx: Ctx): [
    number,
    number
] {
    return [ctx.canvas.width, ctx.canvas.height];
}
/**
 * Draw into a fresh transparent layer that shares the parent's transform, then composite it onto
 * the parent with `blend` and `alpha`.
 */
export function layer(ctx: Ctx, draw: (l: Ctx) => void, { blend = 'source-over', alpha = 1 }: {
    blend?: GlobalCompositeOperation;
    alpha?: number;
} = {}): void {
    if (alpha <= 0)
        return;
    const [w, h] = sizeOf(ctx);
    const canvas = acquire(w, h);
    try {
        const l = canvas.getContext('2d') as unknown as Ctx;
        instrumentLayer(l);
        l.reset();
        l.setTransform(ctx.getTransform());
        draw(l);
        ctx.save();
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.globalCompositeOperation = blend;
        ctx.globalAlpha = alpha;
        ctx.drawImage(canvas, 0, 0);
        ctx.restore();
    }
    finally {
        inUse.delete(canvas);
    }
}
/**
 * Track matte: `content` is visible only where `mask` paints (alpha matte), or only where it does
 * not (`invert`). Both callbacks draw in the parent's coordinate system.
 */
export function matte(ctx: Ctx, content: (l: Ctx) => void, mask: (l: Ctx) => void, { invert = false, alpha = 1, blend = 'source-over', }: {
    invert?: boolean;
    alpha?: number;
    blend?: GlobalCompositeOperation;
} = {}): void {
    layer(ctx, (l) => {
        content(l);
        l.save();
        l.globalCompositeOperation = invert ? 'destination-out' : 'destination-in';
        mask(l);
        l.restore();
    }, { alpha, blend });
}
